/**
 * Шапка квиза одной строкой и счётчик «N/M» со своим видом (слово владельца
 * 21.10.2026: Dance-bit, Calm — «назад» слева, логотип по центру, счётчик
 * справа, полоса под строкой). Поля — `settings.quizHeader.layout = 'logo-row'`,
 * `counterStyle`, `counterFontSize`, `counterFontWeight`, а также цвета и
 * разделитель счётчика `counterColor`, `counterCurrentColor`,
 * `counterTotalColor`, `counterSeparator` (stylepack/header-counter-look) —
 * дверь `resolveQuizHeader`, quizHeader.ts; рисует их QuizScreenWizard.
 */
import React from 'react';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type {
  QuizCounterLook,
  QuizCounterSeparator,
  QuizCounterStyle,
  QuizLogoAlign,
} from './quizHeader';
import { QUIZ_HEADER_COUNTER_DEFAULT_SIZE } from './quizHeader';

interface CounterProps {
  current: number;
  total: number;
  style: QuizCounterStyle;
  fontSize?: number;
  fontWeight?: number;
  /** Цвет текущего номера при `accent-current` — цвет полосы прогресса. */
  accentColor?: string;
  /** Цвета и разделитель (дверь `resolveQuizHeader`); нет — «N/M» как было. */
  look?: QuizCounterLook;
  /** Язык воронки — для слова разделителя `word`. */
  language?: string;
}

/** Текст между номерами по разделителю; `word` — слово словаря плеера. */
const COUNTER_SEPARATOR_TEXT: Record<
  QuizCounterSeparator,
  (language?: string) => string
> = {
  slash: () => '/',
  'slash-spaced': () => ' / ',
  word: (language) => ` ${getPublicQuizStrings(language).counterOf} `,
};

/**
 * Счётчик шага. `plain` без полей — прежний вид байт в байт (12 px, вторичный
 * цвет); `accent-current` — номер цветом полосы, «/всего» — цветом текста.
 * `look`: `color` — цвет всего счётчика, `currentColor` — номер (при
 * accent-current главнее цвета полосы, при plain номер тоже красится),
 * `totalColor` — «всего», `separator` — «/», « / » или слово языка воронки.
 */
export function QuizStepCounter({
  current,
  total,
  style,
  fontSize,
  fontWeight,
  accentColor,
  look,
  language,
}: CounterProps) {
  const accent = style === 'accent-current';
  const currentColor = look?.currentColor;
  const currentPainted = accent || currentColor !== undefined;
  return (
    <span
      data-testid="quiz-step-counter"
      style={{
        fontSize: fontSize ?? QUIZ_HEADER_COUNTER_DEFAULT_SIZE,
        color:
          look?.color ??
          (accent
            ? 'var(--color-text-primary, #1a1a1a)'
            : 'var(--color-text-secondary, #8c8c8c)'),
        ...(fontWeight !== undefined ? { fontWeight } : {}),
      }}
    >
      {currentPainted ? (
        <span
          data-testid="quiz-step-counter-current"
          style={{
            color:
              currentColor ?? accentColor ?? 'var(--color-primary, #1677ff)',
          }}
        >
          {current}
        </span>
      ) : (
        current
      )}
      {COUNTER_SEPARATOR_TEXT[look?.separator ?? 'slash'](language)}
      {look?.totalColor !== undefined ? (
        <span
          data-testid="quiz-step-counter-total"
          style={{ color: look.totalColor }}
        >
          {total}
        </span>
      ) : (
        total
      )}
    </span>
  );
}

interface RowProps {
  back: React.ReactNode;
  logo: React.ReactNode;
  logoAlign: QuizLogoAlign;
  counter: React.ReactNode;
  /** Правее счётчика: «вперёд» и крестик «закрыть». */
  trailing?: React.ReactNode;
}

const cellStyle = (justify: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: justify,
  gap: 8,
  minWidth: 0,
});

/**
 * Строка «назад — логотип — счётчик»: сетка из трёх ячеек `1fr auto 1fr`, так
 * середина стоит по центру строки при любой ширине краёв, а высота строки —
 * по большему из логотипа и кнопки (без распорок и absolute). Логотип слева —
 * после «назад», справа — перед счётчиком.
 */
export function QuizLogoRow({
  back,
  logo,
  logoAlign,
  counter,
  trailing,
}: RowProps) {
  return (
    <div
      data-testid="quiz-header-logo-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div data-row-cell="start" style={cellStyle('flex-start')}>
        {back}
        {logoAlign === 'left' ? logo : null}
      </div>
      <div data-row-cell="center" style={cellStyle('center')}>
        {logoAlign === 'center' ? logo : null}
      </div>
      <div data-row-cell="end" style={cellStyle('flex-end')}>
        {logoAlign === 'right' ? logo : null}
        {counter}
        {trailing}
      </div>
    </div>
  );
}
